import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { generateImage } from '@/lib/protectedIntegrations';
import { SITE_IMAGE_DEFAULTS } from '@/lib/siteImages';
import { Upload, Save, RotateCcw, Loader2, Sparkles } from 'lucide-react';

// Editable preview card for a single admin-editable site image. Lets the
// admin paste a new URL, upload a file, generate via AI, or reset to the
// default, then persist the choice to the Settings entity so the respective
// consumer (hero background / og:image meta) picks it up.
export default function SiteImageCard({ settingKey, title, subtitle, headerBg, previewHeight = 'h-56', defaultUrl, defaultPrompt = '' }) {
  const qc = useQueryClient();
  const { data: settings = [] } = useQuery({
    queryKey: ['settings', 'v4'],
    queryFn: () => base44.entities.Settings.list('-created_date', 1000),
    staleTime: 1000 * 60 * 5,
  });

  const existing = settings.find((s) => s.setting_key === settingKey);
  const currentUrl = (existing && existing.setting_value) || defaultUrl || SITE_IMAGE_DEFAULTS[settingKey];
  const fallback = defaultUrl || SITE_IMAGE_DEFAULTS[settingKey];

  const [url, setUrl] = useState(currentUrl);
  const [aiPrompt, setAiPrompt] = useState(defaultPrompt);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { setUrl(currentUrl); }, [currentUrl]);

  const persist = async (val) => {
    setSaving(true); setMsg(null);
    try {
      if (existing) {
        await base44.entities.Settings.update(existing.id, { setting_value: val });
      } else {
        await base44.entities.Settings.create({ setting_key: settingKey, setting_value: val });
      }
      await qc.invalidateQueries({ queryKey: ['settings'] });
      setMsg('Guardado ✓ — aplicado no respetivo sítio.');
    } catch (e) {
      setMsg('Erro ao guardar (precisa de permissão de admin).');
    } finally { setSaving(false); }
  };

  const save = () => persist(url);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setMsg(null);
    try {
      const res = await base44.integrations.Core.UploadFile({ file });
      setUrl(res.file_url);
    } catch (err) {
      setMsg('Erro no upload.');
    } finally { setUploading(false); }
  };

  const onGenerate = async () => {
    setGenerating(true); setMsg(null);
    try {
      const res = await generateImage({ prompt: aiPrompt || defaultPrompt });
      if (res?.url) {
        setUrl(res.url);
        await persist(res.url);
      } else {
        setMsg('A IA não devolveu uma imagem.');
      }
    } catch (err) {
      setMsg('Erro ao gerar imagem.');
    } finally { setGenerating(false); }
  };

  const reset = () => setUrl(fallback);
  const dirty = url !== currentUrl;
  const busy = saving || uploading || generating;

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
      <div className="text-white px-5 py-3" style={{ background: headerBg }}>
        <p className="font-bold">{title}</p>
        <p className="text-xs opacity-80">{subtitle}</p>
      </div>
      <div className="relative bg-slate-100">
        <img src={url} alt={title} className={`w-full ${previewHeight} object-cover bg-slate-100`} />
        {busy && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
            <Loader2 className="w-7 h-7 animate-spin text-slate-500" />
          </div>
        )}
      </div>
      <div className="p-4 space-y-3 flex-1 flex flex-col">
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="w-full text-xs text-slate-700 border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="URL da imagem" />

        <input
          type="text"
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          className="w-full text-xs text-slate-500 border border-slate-200 rounded-lg px-3 py-2 italic focus:outline-none focus:ring-1 focus:ring-blue-400"
          placeholder="Descrição para gerar com IA…" />

        <div className="flex items-center gap-2 flex-wrap">
          <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-2 rounded-lg transition-colors">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Upload
            <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
          </label>
          <button onClick={reset} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-2 rounded-lg transition-colors">
            <RotateCcw className="w-3.5 h-3.5" /> Repor
          </button>
          <button
            onClick={onGenerate}
            disabled={generating}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
            style={{ background: headerBg }}>
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Gerar IA
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: headerBg }}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Guardar
          </button>
        </div>
        {msg && <p className="text-xs text-slate-600">{msg}</p>}
        <p className="text-xs text-slate-400 break-all mt-auto">Atual: {currentUrl}</p>
      </div>
    </div>
  );
}